<template>
  <iSelect
    v-if="presentation === 'dropdown'"
    v-model="value"
    :items="selectItems"
    value-field="value"
    return-value
    :label="labelValue"
    width="fill"
    display-caret-icon
    :style-overrides="inputStyleOverrides"
  />
  <iRadioBar
    v-else-if="presentation === 'segmented' || presentation === 'chips'"
    v-model="value"
    :label="labelValue"
    :options="radioBarOptions"
    :max-items-per-row="presentation === 'chips' ? -1 : enumOptions.length"
    :style-overrides="radioBarStyleOverrides"
  />
  <iColumn v-else gap="extraSmall" width="fill">
    <iText v-if="labelValue" font-size="small">
      {{ labelValue }}
    </iText>
    <iRadioButton
      v-for="option in enumOptions"
      :key="option"
      v-model="value"
      :value="option"
      :label="option"
      :name="radioGroupName"
    />
  </iColumn>
</template>

<script>
import { plainLabelText } from "%/dynamic-component-editor/helpers/plain-label-text.mjs";
import { DynamicElementMixin } from "%/dynamic-component-editor/mixins/dynamic-element-mixin";
import DynamicElementWithContext from "%/dynamic-component-editor/mixins/dynamic-element-with-context-mixin";
import DynamicAppInputOptionsMixin from "%/dynamic-component-editor/standard-elements/dynamic-app-elements/inputs/dynamic-app-input-options-mixin";
import Label from "%/dynamic-component-editor/options/label";
import EnumInputPresentation from "%/dynamic-component-editor/options/enum-input-presentation";

import SpacingOptions from "%/dynamic-component-editor/bundles/spacing-options";
import AppearanceOptions from "%/dynamic-component-editor/bundles/appearance-options";
import BackgroundColor from "%/dynamic-component-editor/options/background-color";
import BorderColor from "%/dynamic-component-editor/options/border-color";
import BorderWidth from "%/dynamic-component-editor/options/border-width";
import FontColor from "%/dynamic-component-editor/options/font-color";
import FontSize from "%/dynamic-component-editor/options/font-size";
import FontWeight from "%/dynamic-component-editor/options/font-weight";

export default {
  name: "DynamicAppEnumInputElement",
  mixins: [DynamicElementMixin, DynamicElementWithContext, DynamicAppInputOptionsMixin],
  elementPanelSettings: {
    displayName: "Option Picker",
    elementGroup: "general",
    displayIcon: "list",
    elementType: "element",
  },
  hidden: true,
  options: [
    Label,
    EnumInputPresentation,
    BackgroundColor,
    BorderColor,
    BorderWidth,
    FontColor,
    FontSize,
    FontWeight,
  ],
  optionBundles: [
    new SpacingOptions(),
    new AppearanceOptions(),
  ],
  computed: {
    labelValue() {
      return plainLabelText(this.getOptionValue(Label));
    },
    presentation() {
      return this.getOptionValue(EnumInputPresentation) ?? EnumInputPresentation.defaultValue;
    },
    enumOptions() {
      const field = this.relatedEntity?.fields?.[this.elementContext.field];
      return Array.isArray(field?.enumOptions) ? field.enumOptions : [];
    },
    selectItems() {
      return this.enumOptions.map(option => ({ name: option, value: option }));
    },
    radioBarOptions() {
      return this.enumOptions.map(option => ({ value: option, label: option }));
    },
    radioGroupName() {
      return `${this.elementContext.entityContext}-${this.elementContext.field}`;
    },
    inputStyleOverrides() {
      const padding = this.appInputPadding;
      const margins = this.appInputMargins;
      return this.appInputStyleOverrides({
        inputBorderRadius: this.appInputBorderRadius,
        dropdownBorderRadius: this.appInputBorderRadius,
        inputBackgroundColor: this.appInputBackgroundColor,
        inputHoverBackgroundColor: this.appInputBackgroundColor,
        inputBorderColor: this.appInputBorderColor,
        inputBorderWidth: this.appInputBorderWidth,
        inputFontColor: this.appInputFontColor,
        inputFontWeight: this.appInputFontWeight,
        standardInputFontSize: this.appInputFontSize,
        standardInputPaddingTop: padding.top ?? null,
        standardInputPaddingBottom: padding.bottom ?? null,
        standardInputPaddingLeft: padding.left ?? null,
        standardInputPaddingRight: padding.right ?? null,
        marginTop: margins.marginTop ?? null,
        marginBottom: margins.marginBottom ?? null,
        marginLeft: margins.marginLeft ?? null,
        marginRight: margins.marginRight ?? null,
      });
    },
    radioBarStyleOverrides() {
      return Object.fromEntries(Object.entries({
        barBackgroundColor: this.appInputBackgroundColor,
        activeItemBorderColor: this.appInputBorderColor,
      }).filter(([, value]) => value !== null && value !== undefined));
    },
    value: {
      get() {
        return this.valueFromContext;
      },
      set(value) {
        if (!this.editMode) {
          this.valueFromContext = value;
        }
      },
    },
  },
};
</script>

<style lang="scss" scoped>
</style>
